import React, { useState } from 'react';
import { FieldHelpModal } from './FieldHelpModal';
import { FIELD_HELP_CONTENT } from '../data/helpContent';
import { IndustryName } from '../../../data/industryDefaults';

interface SalaryVacancyGroupProps {
  avgSalary: number | string;
  dailyVacancyCost: number | string;
  industryName: string;
  onSalaryChange: (value: string) => void;
  onVacancyCostChange: (value: string) => void;
  onCalculatorClick: () => void;
  formatWithCommas: (value: number | string) => string;
  handleInputSelection: (e: React.FocusEvent<HTMLInputElement>) => void;
  handleInputClick: (e: React.MouseEvent<HTMLInputElement>) => void;
  calculationBasis?: any;
}

export const SalaryVacancyGroup: React.FC<SalaryVacancyGroupProps> = ({
  avgSalary,
  dailyVacancyCost,
  industryName,
  onSalaryChange,
  onVacancyCostChange,
  onCalculatorClick,
  formatWithCommas,
  handleInputSelection,
  handleInputClick,
  calculationBasis
}) => {
  const [activeHelpField, setActiveHelpField] = useState<string | null>(null);
  const hasCalculated = !!calculationBasis;

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-xl font-bold mb-2 text-gray-800 border-b pb-3 border-gray-200">
        <span className="text-sky-600 mr-2">③</span>Compensation & Vacancy Costs
      </h3>
      <p className="text-sm text-gray-600 mb-5">
        Calculate accurate salary and vacancy costs based on your hiring profile.
      </p>

      {/* Calculate Values Section */}
      {!hasCalculated ? (
        <div className="py-8 text-center">
          <div className="mb-6">
            <svg className="w-16 h-16 mx-auto text-sky-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">
              Ready to calculate your compensation metrics?
            </h4>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              Set your baseline compensation metrics based on your specific hiring profile (entry/mid/senior mix) and location. These values will be used for all ROI calculations.
            </p>
          </div>
          <button
            type="button"
            onClick={onCalculatorClick}
            className="px-8 py-3 bg-sky-600 text-white font-medium rounded-md hover:bg-sky-700 transition-all duration-200 inline-flex items-center gap-2 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Calculate Values
          </button>
        </div>
      ) : (
        <>
          {/* Recalculate Button - Smaller, positioned at top when fields are shown */}
          <div className="mb-4 flex justify-end">
            <button
              type="button"
              onClick={onCalculatorClick}
              className="px-4 py-2 bg-sky-100 text-sky-700 font-medium rounded-md hover:bg-sky-200 transition-colors inline-flex items-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Recalculate
            </button>
          </div>

          {/* Average Salary Field */}
          <div className="mb-4 animate-fadeIn">
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm font-medium text-gray-700">
                Average Salary for Open Position ($) <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setActiveHelpField('avgSalary')}
                className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                title="Click for help"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9,]*"
              name="avgSalary"
              value={formatWithCommas(avgSalary)}
              onChange={(e) => onSalaryChange(e.target.value)}
              onFocus={handleInputSelection}
              onClick={handleInputClick}
              className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              placeholder="e.g., 90,000"
            />
            <p className="text-xs text-gray-600 mt-1">Annual salary for positions you typically hire</p>
          </div>

          {/* Daily Vacancy Cost Field */}
          <div className="animate-fadeIn">
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm font-medium text-gray-700">
                Daily Vacancy Cost ($) <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setActiveHelpField('dailyVacancyCost')}
                className="ml-1 text-sky-500 hover:text-sky-700 inline-flex"
                title="Click for help"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            </div>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9,]*"
              name="dailyVacancyCost"
              value={formatWithCommas(dailyVacancyCost)}
              onChange={(e) => onVacancyCostChange(e.target.value)}
              onFocus={handleInputSelection}
              onClick={handleInputClick}
              className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
              placeholder="e.g., 616"
            />
            <p className="text-xs text-gray-600 mt-1">
              Total daily cost of an unfilled position including lost productivity
            </p>
          </div>
        </>
      )}

      {/* Help Modal */}
      <FieldHelpModal
        isOpen={!!activeHelpField}
        onClose={() => setActiveHelpField(null)}
        content={activeHelpField ? FIELD_HELP_CONTENT[activeHelpField]?.(industryName as IndustryName) : null}
        industryName={industryName}
      />
    </div>
  );
};